import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import { Loader2, Grid3x3, List, Map as MapIcon, X } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ProfessionalCard } from '@/components/professionals/ProfessionalCard';
import { BrutalMultiSelect } from '@/components/ui/BrutalMultiSelect';
import { Checkbox } from '@/components/ui/checkbox';
import { SEO } from '@/components/SEO';
import { MapView } from '@/components/professionals/MapView';
import { useProfessionalsSearch, ITEMS_PER_PAGE } from '@/hooks/useProfessionalsSearch';
import { useCategoryOptions, useLanguageOptions } from '@/hooks/useActiveCategories';
import { BrutalToggleGroup } from '@/components/ui/BrutalToggleGroup';
import { LANGUAGE_CODE_TO_NAME } from '@/data/languages';
import {
  BrutalSelect,
  BrutalSelectTrigger,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectValue,
} from '@/components/ui/BrutalSelect';

// Use dynamic language mapping from data file
const LANGUAGES_MAP = LANGUAGE_CODE_TO_NAME;

const Professionals = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  // Initialize states from URL params
  const [searchInputValue, setSearchInputValue] = useState(() => searchParams.get('q') || '');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [cityInputValue, setCityInputValue] = useState('');
  const [city, setCity] = useState('');
  const [page, setPage] = useState(1);
  
  // Filters - initialize category from URL
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    const categoryParam = searchParams.get('category');
    return categoryParam ? [categoryParam] : [];
  });
  const [radius, setRadius] = useState(50);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [entityType, setEntityType] = useState<'all' | 'professionals' | 'companies'>('all');
  const [useLocation, setUseLocation] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [sortBy, setSortBy] = useState<'relevance' | 'distance' | 'recent'>('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  
  const currentLanguage = (i18n.language || 'it').substring(0, 2) as 'it' | 'en' | 'es';

  // Debounce search query - 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInputValue !== searchQuery) {
        setSearchQuery(searchInputValue);
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInputValue, searchQuery]);

  // Debounce city - 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (cityInputValue !== city) {
        setCity(cityInputValue);
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [cityInputValue, city]);

  // Use active categories and languages hooks
  const { options: categoryOptions, isLoading: categoriesLoading } = useCategoryOptions(currentLanguage);
  const { options: languageOptions, isLoading: languagesLoading } = useLanguageOptions();

  // Use TanStack Query for data fetching with caching
  const { data, isLoading, isFetching } = useProfessionalsSearch({
    page,
    searchQuery,
    selectedCategories,
    city,
    selectedLanguages,
    entityType,
    sortBy,
    userLocation,
    radius,
  });

  const professionals = data?.results || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  // Update state when URL params change (e.g., navigating with new params)
  useEffect(() => {
    const qParam = searchParams.get('q');
    const categoryParam = searchParams.get('category');
    
    if (qParam !== null && qParam !== searchQuery) {
      setSearchInputValue(qParam);
      setSearchQuery(qParam);
    }
    if (categoryParam !== null) {
      if (!selectedCategories.includes(categoryParam)) {
        setSelectedCategories([categoryParam]);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (useLocation && !userLocation) {
      getUserLocation();
    }
  }, [useLocation]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast({
            title: t('common.error'),
            description: t('search.geolocationError'),
            variant: 'error',
          });
          setUseLocation(false);
        }
      );
    }
  };

  const clearFilters = () => {
    setSearchInputValue('');
    setSearchQuery('');
    setSelectedCategories([]);
    setCityInputValue('');
    setCity('');
    setSelectedLanguages([]);
    setEntityType('all');
    setUseLocation(false);
    setUserLocation(null);
    setPage(1);
  };

  const toggleLanguage = (langCode: string) => {
    setSelectedLanguages(prev =>
      prev.includes(langCode)
        ? prev.filter(l => l !== langCode)
        : [...prev, langCode]
    );
    setPage(1);
  };
  
  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
    setPage(1);
  };

  const getAppliedFilters = () => {
    const filters: { key: string; label: string }[] = [];
    selectedCategories.forEach(catId => {
      // Use categoryOptions from DB instead of static getCategoryById
      const catOption = categoryOptions.find(c => c.value === catId);
      if (catOption) {
        filters.push({ key: `category-${catId}`, label: catOption.label });
      } else {
        // Fallback to category ID if not found
        filters.push({ key: `category-${catId}`, label: catId });
      }
    });
    if (city) filters.push({ key: 'city', label: city });
    if (entityType !== 'all') {
      filters.push({ 
        key: 'entityType', 
        label: entityType === 'professionals' ? t('filters.professionals') : t('filters.companies') 
      });
    }
    selectedLanguages.forEach(lang => {
      const langOption = languageOptions.find(l => l.value === lang);
      const langName = langOption?.label || LANGUAGES_MAP[lang] || lang;
      filters.push({ key: `lang-${lang}`, label: langName });
    });
    return filters;
  };

  const removeFilter = (key: string) => {
    if (key.startsWith('category-')) {
      const catId = key.replace('category-', '');
      setSelectedCategories(prev => prev.filter(c => c !== catId));
    } else if (key === 'city') {
      setCity('');
      setCityInputValue('');
    } else if (key === 'entityType') setEntityType('all');
    else if (key.startsWith('lang-')) {
      const langCode = key.replace('lang-', '');
      setSelectedLanguages(prev => prev.filter(l => l !== langCode));
    }
    setPage(1);
  };

  const appliedFilters = getAppliedFilters();

  // Show skeleton only on initial load, not on filter changes
  const showSkeleton = isLoading && professionals.length === 0;

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={t('professionals.seoTitle')}
        description={t('professionals.seoDescription')}
      />
      <Header />
      
      <SidebarProvider defaultOpen={false}>
        <div className="flex w-full bg-surface">
          {/* SIDEBAR COLLAPSIBLE */}
          <Sidebar 
            className="border-r-[4px] border-dark shadow-[4px_0px_0px_0px] shadow-dark" 
            collapsible="offcanvas"
          >
            <SidebarHeader className="border-b-[3px] border-dark bg-white shadow-[0px_2px_0px_0px] shadow-dark p-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-xl uppercase tracking-wide">
                  {t('filters.title')}
                </h2>
                <SidebarTrigger>
                  <X className="h-5 w-5" />
                </SidebarTrigger>
              </div>
            </SidebarHeader>

            <SidebarContent className="p-3">
              <div className="space-y-6">
                {/* Search */}
                <div>
                  <BrutalInput
                    type="text"
                    placeholder={t('search.placeholder')}
                    value={searchInputValue}
                    onChange={(e) => setSearchInputValue(e.target.value)}
                  />
                </div>

                {/* Category */}
                <div>
                  <h3 className="font-heading font-bold mb-3">{t('filters.category')}</h3>
                  <BrutalMultiSelect
                    options={categoryOptions}
                    selected={selectedCategories}
                    onChange={(values) => {
                      setSelectedCategories(values);
                      setPage(1);
                    }}
                    placeholder={categoriesLoading ? t('common.loading') : t('filters.selectCategories')}
                    searchPlaceholder={t('common.search')}
                    emptyMessage={categoriesLoading ? t('common.loading') : t('filters.noCategoriesFound')}
                    disabled={categoriesLoading}
                  />
                </div>

                {/* Location */}
                <div>
                  <h3 className="font-heading font-bold mb-3">{t('filters.location')}</h3>
                  <BrutalInput
                    type="text"
                    placeholder={t('filters.cityPlaceholder')}
                    value={cityInputValue}
                    onChange={(e) => setCityInputValue(e.target.value)}
                    className="mb-3"
                  />
                  <label className="flex items-center gap-2 mb-3">
                    <Checkbox
                      checked={useLocation}
                      onCheckedChange={(checked) => setUseLocation(checked as boolean)}
                    />
                    <span className="text-sm">{t('filters.useMyLocation')}</span>
                  </label>
                  {useLocation && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('filters.radius')}: {radius}km
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="100"
                        step="5"
                        value={radius}
                        onChange={(e) => {
                          setRadius(parseInt(e.target.value));
                          setPage(1);
                        }}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>

                {/* Languages */}
                <div>
                  <h3 className="font-heading font-bold mb-3">{t('filters.languages')}</h3>
                  <BrutalMultiSelect
                    options={languageOptions}
                    selected={selectedLanguages}
                    onChange={(values) => {
                      setSelectedLanguages(values);
                      setPage(1);
                    }}
                    placeholder={languagesLoading ? t('common.loading') : t('filters.selectLanguages')}
                    searchPlaceholder={t('common.search')}
                    emptyMessage={languagesLoading ? t('common.loading') : t('filters.noLanguagesFound')}
                    disabled={languagesLoading}
                  />
                </div>

                {/* Entity Type Toggle */}
                <div>
                  <h3 className="font-heading font-bold mb-3">{t('filters.entityType')}</h3>
                  <BrutalToggleGroup
                    options={[
                      { value: 'all', label: t('filters.all') },
                      { value: 'professionals', label: t('filters.professionals') },
                      { value: 'companies', label: t('filters.companies') },
                    ]}
                    value={entityType}
                    onChange={(value) => {
                      setEntityType(value as 'all' | 'professionals' | 'companies');
                      setPage(1);
                    }}
                  />
                </div>

                {/* Clear Filters Button */}
                <div>
                  <BrutalButton
                    onClick={clearFilters}
                    variant="outline"
                    className="w-full mb-3"
                  >
                    {t('filters.clearFilters')}
                  </BrutalButton>
                </div>
              </div>
            </SidebarContent>
          </Sidebar>

          {/* MAIN CONTENT */}
          <main className="flex-1 overflow-auto">
            {/* CONTENUTO PRINCIPALE */}
            <div className={`container mx-auto ${viewMode === 'map' ? 'px-0 py-4 pt-12' : 'px-4 py-12'}`}>
              {/* Hero Section */}
              <div className={`text-center mb-12 ${viewMode === 'map' ? 'px-4' : ''}`}>
                <SEOBreadcrumb items={[{ label: t('common.professionals') }]} />
                <h1 className="font-heading font-bold text-4xl md:text-6xl text-dark mb-4 flex items-center justify-center gap-3">
                  {t('professionals.heroTitle')}
                  {isFetching && !isLoading && (
                    <Loader2 className="w-5 h-5 md:w-6 md:h-6 animate-spin text-muted-foreground" />
                  )}
                </h1>
                <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
                  {t('professionals.heroSubtitle')}
                </p>
              </div>

              {/* Header Results */}
              <div className={`flex flex-col gap-4 ${viewMode === 'map' ? 'mt-12 mb-3 px-4' : 'mb-6'}`}>
                {/* Desktop: conteggio a sinistra, pulsanti a destra */}
                <div className="hidden lg:flex lg:flex-row lg:justify-between lg:items-start">
                  <div>
                    <h2 className="font-heading text-2xl font-bold mb-2 flex items-center gap-2">
                      {totalCount} {t('search.professionalsFound')}
                      {isFetching && !isLoading && (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      )}
                    </h2>
                    {appliedFilters.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {appliedFilters.map(filter => (
                          <span
                            key={filter.key}
                            className="bg-surface px-3 py-1 border-[2px] border-dark text-sm flex items-center gap-2"
                          >
                            {filter.label}
                            <button
                              onClick={() => removeFilter(filter.key)}
                              className="hover:text-primary"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    {/* View Toggle */}
                    <div className="flex border-[3px] border-dark">
                      <SidebarTrigger className="px-3 py-2 bg-card border-0 h-auto w-auto hover:bg-card flex items-center justify-center" />
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-3 py-2 border-l-[3px] border-dark flex items-center justify-center ${viewMode === 'grid' ? 'bg-primary' : 'bg-card'}`}
                        title="Griglia"
                      >
                        <Grid3x3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 border-l-[3px] border-dark flex items-center justify-center ${viewMode === 'list' ? 'bg-primary' : 'bg-card'}`}
                        title="Lista"
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('map')}
                        className={`px-3 py-2 border-l-[3px] border-dark flex items-center justify-center ${viewMode === 'map' ? 'bg-primary' : 'bg-card'}`}
                        title="Mappa"
                      >
                        <MapIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Sort */}
                    <BrutalSelect value={sortBy} onValueChange={(v) => { setSortBy(v as any); setPage(1); }}>
                      <BrutalSelectTrigger className="w-auto min-w-[160px]">
                        <BrutalSelectValue />
                      </BrutalSelectTrigger>
                      <BrutalSelectContent>
                        <BrutalSelectItem value="relevance">{t('sort.relevance')}</BrutalSelectItem>
                        <BrutalSelectItem value="distance">{t('sort.distance')}</BrutalSelectItem>
                        <BrutalSelectItem value="recent">{t('sort.recent')}</BrutalSelectItem>
                      </BrutalSelectContent>
                    </BrutalSelect>
                  </div>
                </div>

                {/* Mobile/Tablet: pulsanti centrati */}
                <div className="flex lg:hidden justify-center">
                  <div className="flex gap-2">
                    {/* View Toggle */}
                    <div className="flex border-[3px] border-dark">
                      <SidebarTrigger className="px-2 py-2 bg-card border-0 h-auto w-auto hover:bg-card flex items-center justify-center" />
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-2 py-2 border-l-[3px] border-dark flex items-center justify-center ${viewMode === 'grid' ? 'bg-primary' : 'bg-card'}`}
                        title="Griglia"
                      >
                        <Grid3x3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('map')}
                        className={`px-2 py-2 border-l-[3px] border-dark flex items-center justify-center ${viewMode === 'map' ? 'bg-primary' : 'bg-card'}`}
                        title="Mappa"
                      >
                        <MapIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Sort */}
                    <BrutalSelect value={sortBy} onValueChange={(v) => { setSortBy(v as any); setPage(1); }}>
                      <BrutalSelectTrigger className="w-auto min-w-[140px] text-sm">
                        <BrutalSelectValue />
                      </BrutalSelectTrigger>
                      <BrutalSelectContent>
                        <BrutalSelectItem value="relevance">{t('sort.relevance')}</BrutalSelectItem>
                        <BrutalSelectItem value="distance">{t('sort.distance')}</BrutalSelectItem>
                        <BrutalSelectItem value="recent">{t('sort.recent')}</BrutalSelectItem>
                      </BrutalSelectContent>
                    </BrutalSelect>
                  </div>
                </div>

                {/* Mobile/Tablet: conteggio sotto i pulsanti */}
                <div className="lg:hidden text-center">
                  <h2 className="font-heading text-lg font-bold flex items-center justify-center gap-2">
                    {totalCount} {t('search.professionalsFound')}
                    {isFetching && !isLoading && (
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                  </h2>
                  {appliedFilters.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 justify-center">
                      {appliedFilters.map(filter => (
                        <span
                          key={filter.key}
                          className="bg-surface px-3 py-1 border-[2px] border-dark text-sm flex items-center gap-2"
                        >
                          {filter.label}
                          <button
                            onClick={() => removeFilter(filter.key)}
                            className="hover:text-primary"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Loading States & Results */}
              {showSkeleton ? (
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8' : 'space-y-4 mb-8'}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="h-80 w-full" />
                  ))}
                </div>
              ) : viewMode === 'map' ? (
                <div className="h-auto">
                  <MapView professionals={professionals} />
                </div>
              ) : professionals.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-lg text-muted-foreground">{t('search.noResults')}</p>
                  <BrutalButton onClick={clearFilters} className="mt-4">
                    {t('filters.clearFilters')}
                  </BrutalButton>
                </div>
              ) : (
                <>
                  <div className={viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8' 
                    : 'space-y-4 mb-8'
                  }>
                    {professionals.map((professional: any) => (
                      <ProfessionalCard
                        key={professional.id}
                        data={professional}
                        layout={viewMode === 'list' ? 'horizontal' : 'vertical'}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 py-8">
                      <BrutalButton
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        variant="outline"
                      >
                        {t('pagination.previous')}
                      </BrutalButton>
                      <span className="flex items-center px-4 font-bold">
                        {page} / {totalPages}
                      </span>
                      <BrutalButton
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        variant="outline"
                      >
                        {t('pagination.next')}
                      </BrutalButton>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </SidebarProvider>
      
      <Footer />
    </div>
  );
};

export default Professionals;
